import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { Loading, Notice } from './components';
import { Shell } from './layout/Shell';
import { useSession } from './auth/session';
import { ChangePassword } from './pages/ChangePassword';
import { Dashboard } from './pages/Dashboard';
import { Forms } from './pages/Forms';
import { LocationRequests } from './pages/LocationRequests';
import { QuestionLibrary } from './pages/QuestionLibrary';
import { Escalations } from './pages/Escalations';
import { Reports } from './pages/Reports';
import { Rules } from './pages/Rules';
import { Roles } from './pages/Roles';
import { Schedule } from './pages/Schedule';
import { AuditTrail, OrgUnits, Policies, Webhooks } from './pages/Settings';
import { SignIn } from './pages/SignIn';
import { Sites } from './pages/Sites';
import { Tags } from './pages/Tags';
import { Submissions } from './pages/Submissions';
import { Users } from './pages/Users';
import { VisitRules } from './pages/VisitRules';

/**
 * Routing, and the two gates in front of it.
 *
 * A signed-out user sees the sign-in screen and nothing else. A user whose password was
 * issued by an administrator sees the change-password screen and nothing else, because an
 * account whose password someone else chose is one that person can still sign into.
 *
 * Route-level permission checks are deliberately absent. The server refuses every request
 * a user is not entitled to make, and duplicating that here would create two sources of
 * truth that drift — SMA hid menu items and treated that as authorisation, which is why
 * typing a URL was enough to reach anything (S-2).
 */

function Guarded(): JSX.Element {
  const { user, loading } = useSession();

  if (loading) return <Loading />;
  if (user === null) return <Navigate to="/sign-in" replace />;
  if (user.mustChangePassword) return <ChangePassword forced />;

  return <Shell />;
}

function SignInGate(): JSX.Element {
  const { user, loading } = useSession();

  if (loading) return <Loading />;
  // Already signed in — send them on rather than showing a form that would confuse.
  if (user !== null) return <Navigate to="/" replace />;

  return <SignIn />;
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in" element={<SignInGate />} />

        <Route element={<Guarded />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/submissions" element={<Submissions />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/org-units" element={<OrgUnits />} />
          <Route path="/users" element={<Users />} />
          <Route path="/roles" element={<Roles />} />
          <Route path="/forms" element={<Forms />} />
          <Route path="/library" element={<QuestionLibrary />} />
          <Route path="/location-requests" element={<LocationRequests />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/visit-rules" element={<VisitRules />} />
          <Route path="/escalations" element={<Escalations />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/webhooks" element={<Webhooks />} />
          <Route path="/audit" element={<AuditTrail />} />
          <Route path="/change-password" element={<ChangePassword />} />

          <Route
            path="*"
            element={<Notice kind="error">That page does not exist.</Notice>}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
